import { isBefore, areIntervalsOverlapping, parseISO } from 'date-fns';
import type {
  OpenShiftNotAvailableReason,
  OpenShiftState,
  OpenShiftStateAndReason,
} from '../../../components/Shifts/Common/types';
import type { SchedulerOpenShiftFieldsFragment } from '../../../__generated__/graphql';
import { Theme } from '../../../../../theme';

const OPEN_SHIFT_STATE = {
  AVAILABLE: 'available',
  REQUESTED: 'requested',
  CONFIRMED: 'confirmed',
  NOT_AVAILABLE: 'not-available',
} as const satisfies Record<string, OpenShiftState>;

const NOT_AVAILABLE_REASON = {
  PAST_SHIFT: 'past-shift',
  OVERLAPPED: 'overlapped',
  NO_FREE_SLOTS: 'no-free-slots',
} as const satisfies Record<string, OpenShiftNotAvailableReason>;

type ShiftInterval = {
  start: Date;
  end: Date;
};

export const calculateOpenShiftState = (
  shift: SchedulerOpenShiftFieldsFragment,
  meId: string,
  myShiftIntervals: ShiftInterval[],
): OpenShiftStateAndReason => {
  const shiftEnd = parseISO(shift.flair__End_Datetime__c);

  if (isBefore(shiftEnd, new Date())) {
    return {
      state: OPEN_SHIFT_STATE.NOT_AVAILABLE,
      notAvailableReason: NOT_AVAILABLE_REASON.PAST_SHIFT,
    };
  }

  const isConfirmed = shift.Shifts.some(
    (s) => s.flair__Resource_Employee__c === meId,
  );
  if (isConfirmed) {
    return { state: OPEN_SHIFT_STATE.CONFIRMED };
  }

  const shiftStart = parseISO(shift.flair__Start_Datetime__c);
  const isOverlapping = myShiftIntervals.some((interval) =>
    areIntervalsOverlapping(
      { start: shiftStart, end: shiftEnd },
      { start: interval.start, end: interval.end },
    ),
  );

  if (isOverlapping) {
    return {
      state: OPEN_SHIFT_STATE.NOT_AVAILABLE,
      notAvailableReason: NOT_AVAILABLE_REASON.OVERLAPPED,
    };
  }

  const hasUnconfirmedRequest = shift.AssignmentRequests.some(
    (r) => r.flair__Employee__c === meId && r.flair__Confirmed_At__c === null,
  );
  if (hasUnconfirmedRequest) {
    return { state: OPEN_SHIFT_STATE.REQUESTED };
  }

  const assignedCount = shift.Shifts.length;
  const hasFreeSlots = shift.flair__Employees_Count__c > assignedCount;

  return {
    state: hasFreeSlots
      ? OPEN_SHIFT_STATE.AVAILABLE
      : OPEN_SHIFT_STATE.NOT_AVAILABLE,
    notAvailableReason: hasFreeSlots
      ? undefined
      : NOT_AVAILABLE_REASON.NO_FREE_SLOTS,
  };
};

const STATE_COLORS: Record<OpenShiftState, string> = {
  [OPEN_SHIFT_STATE.AVAILABLE]: Theme.colorHex.teal1,
  [OPEN_SHIFT_STATE.REQUESTED]: Theme.colorHex.accentHoney,
  [OPEN_SHIFT_STATE.CONFIRMED]: Theme.colorHex.accentTeal,
  [OPEN_SHIFT_STATE.NOT_AVAILABLE]: Theme.colorHex.gray2,
};

export const getOpenShiftEventColor = (state: OpenShiftState): string =>
  STATE_COLORS[state];
